/**
 * API Routes
 * 
 * API endpoint routes (no locale prefix, as APIs are locale-agnostic).
 * All API routes should be defined here for centralized management.
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || (typeof window !== 'undefined' ? `${window.location.origin}/api` : "https://jubiel.local/api");

/**
 * Get the API base URL
 * @returns The API base URL from environment variable or default
 */
export function getApiBaseUrl(): string {
  return API_BASE_URL;
}

/**
 * JUB-144: the event cover and gift image endpoints, addressed by uuid with one
 * image slot each (POST uploads/replaces, DELETE clears). Defined once and
 * shared by the absolute and relative route maps below.
 */
const MEDIA_ROUTES = {
  cover: (eventUuid: string) => `/events/${eventUuid}/cover`,
  giftImage: (eventUuid: string, giftUuid: string) => `/events/${eventUuid}/gifts/${giftUuid}/image`,
} as const;

export const API_ROUTES = {
  /**
   * Authentication endpoints
   */
  auth: {
    login: () => `${API_BASE_URL}/auth/login`,
    register: () => `${API_BASE_URL}/auth/register`,
    logout: () => `${API_BASE_URL}/auth/logout`,
    refresh: () => `${API_BASE_URL}/auth/refresh`,
    verifyEmail: () => `${API_BASE_URL}/auth/verify-email`,
    forgotPassword: () => `${API_BASE_URL}/auth/forgot-password`,
    resetPassword: () => `${API_BASE_URL}/auth/reset-password/confirm`,
  },

  /**
   * User endpoints
   */
  user: {
    profile: () => `${API_BASE_URL}/user/profile`,
    settings: () => `${API_BASE_URL}/user/settings`,
  },

  /**
   * Event endpoints
   */
  events: {
    list: () => `${API_BASE_URL}/events`,
    create: () => `${API_BASE_URL}/events`,
    show: (id: string) => `${API_BASE_URL}/events/${id}`,
    update: (id: string) => `${API_BASE_URL}/events/${id}`,
    delete: (id: string) => `${API_BASE_URL}/events/${id}`,
    publish: (id: string) => `${API_BASE_URL}/events/${id}/publish`,
    unpublish: (id: string) => `${API_BASE_URL}/events/${id}/unpublish`,
    qrCode: (id: string) => `${API_BASE_URL}/events/${id}/qr-code`,
    share: (id: string) => `${API_BASE_URL}/events/${id}/share`,
    landingSettings: (id: string) => `${API_BASE_URL}/events/${id}/landing-settings`,
    rsvpSettings: (id: string) => `${API_BASE_URL}/events/${id}/rsvp-settings`,
    giftRegistry: (id: string) => `${API_BASE_URL}/events/${id}/gift-registry`,
    cover: (eventUuid: string) => `${API_BASE_URL}${MEDIA_ROUTES.cover(eventUuid)}`,
    public: (slug: string) => `${API_BASE_URL}/events/public/${slug}`,
  },

  /**
   * Gift endpoints
   */
  gifts: {
    list: (eventId: string) => `${API_BASE_URL}/events/${eventId}/gifts`,
    reserve: (giftId: string) => `${API_BASE_URL}/gifts/${giftId}/reserve`,
    contributions: (giftId: string) => `${API_BASE_URL}/gifts/${giftId}/contributions`,
    image: (eventUuid: string, giftUuid: string) => `${API_BASE_URL}${MEDIA_ROUTES.giftImage(eventUuid, giftUuid)}`,
  },

  /**
   * Guest endpoints
   */
  guests: {
    list: (eventId: string) => `${API_BASE_URL}/events/${eventId}/guests`,
    import: (eventId: string) => `${API_BASE_URL}/events/${eventId}/guests/import`,
    sendInvitations: (eventId: string) => `${API_BASE_URL}/events/${eventId}/invitations/send`,
  },

  /**
   * Payment endpoints
   */
  payments: {
    createIntent: () => `${API_BASE_URL}/payments/create`,
    confirm: () => `${API_BASE_URL}/payments/confirm`,
    testComplete: () => `${API_BASE_URL}/payments/test-complete`,
  },

  /**
   * Payment method endpoints
   */
  paymentMethods: {
    list: () => `${API_BASE_URL}/payment-methods`,
    create: () => `${API_BASE_URL}/payment-methods`,
    update: (id: string) => `${API_BASE_URL}/payment-methods/${id}`,
    delete: (id: string) => `${API_BASE_URL}/payment-methods/${id}`,
  },

  /**
   * Subscription endpoints
   */
  subscriptions: {
    plans: () => `${API_BASE_URL}/subscriptions/plans`,
  },

  /**
   * Template endpoints (for regular users)
   */
  templates: {
    list: () => `${API_BASE_URL}/templates`,
    apply: (eventId: string) => `${API_BASE_URL}/events/${eventId}/template`,
  },

  /**
   * Public-safe feature flags (customer-facing subset)
   */
  features: {
    list: () => `${API_BASE_URL}/features`,
  },

  /**
   * Management endpoints (superadmin only)
   */
  management: {
    // Soft-deleted resources
    deletedAccounts: (params?: Record<string, string | number>) => {
      const url = `${API_BASE_URL}/management/accounts/deleted`;
      if (!params || Object.keys(params).length === 0) return url;
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      return `${url}?${searchParams.toString()}`;
    },
    deletedEvents: (params?: Record<string, string | number>) => {
      const url = `${API_BASE_URL}/management/events/deleted`;
      if (!params || Object.keys(params).length === 0) return url;
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      return `${url}?${searchParams.toString()}`;
    },
    restoreAccount: (id: number) => `${API_BASE_URL}/management/accounts/${id}/restore`,
    restoreEvent: (uuid: string) => `${API_BASE_URL}/management/events/${uuid}/restore`,

    featureFlags: () => `${API_BASE_URL}/management/feature-flags`,
    featureFlagToggle: (key: string) => `${API_BASE_URL}/management/feature-flags/${key}`,
    settings: () => `${API_BASE_URL}/management/settings`,
    users: (params?: Record<string, string | number>) => {
      const url = `${API_BASE_URL}/management/users`;
      if (!params || Object.keys(params).length === 0) return url;
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) searchParams.append(key, String(value));
      });
      return `${url}?${searchParams.toString()}`;
    },
    userProfile: (id: number) => `${API_BASE_URL}/management/users/${id}`,
    updateUserStatus: (id: number) => `${API_BASE_URL}/management/users/${id}`,

    // Plan management (legacy account-level, JUB-42)
    getPlan: (id: number) => `${API_BASE_URL}/management/accounts/${id}/plan`,
    updatePlan: (id: number) => `${API_BASE_URL}/management/accounts/${id}/plan`,

    // Plan management (per-event, the mechanism that actually gates
    // feature access — JUB-138/JUB-142)
    getEventPlan: (uuid: string) => `${API_BASE_URL}/management/events/${uuid}/plan`,
    updateEventPlan: (uuid: string) => `${API_BASE_URL}/management/events/${uuid}/plan`,

    // Revenue / payment overview (JUB-43)
    revenue: () => `${API_BASE_URL}/management/revenue`,

    // Template management
    templates: {
      list: () => `${API_BASE_URL}/management/templates`,
      preview: (id: number) => `${API_BASE_URL}/management/templates/${id}/preview`,
      update: (id: number) => `${API_BASE_URL}/management/templates/${id}`,
      delete: (id: number) => `${API_BASE_URL}/management/templates/${id}`,
      install: () => `${API_BASE_URL}/management/templates/install`,
      // JUB-161: dry-run validation, starter kit and package export
      validate: () => `${API_BASE_URL}/management/templates/validate`,
      starter: (key: string) => `${API_BASE_URL}/management/templates/starter/${encodeURIComponent(key)}`,
      export: (id: number) => `${API_BASE_URL}/management/templates/${id}/export`,
    },
  },

  /**
   * RSVP endpoints (public)
   */
  rsvp: {
    show: (token: string) => `${API_BASE_URL}/rsvp/${token}`,
    submit: (token: string) => `${API_BASE_URL}/rsvp/${token}`,
  },
} as const;

/**
 * API Routes - Relative paths (for apiClient calls)
 * These are the same routes but without the base URL prefix
 */
export const API_ROUTES_RELATIVE = {
  auth: {
    login: () => `/auth/login`,
    register: () => `/auth/register`,
    logout: () => `/auth/logout`,
    refresh: () => `/auth/refresh`,
    verifyEmail: () => `/auth/verify-email`,
    forgotPassword: () => `/auth/forgot-password`,
    resetPassword: () => `/auth/reset-password/confirm`,
  },
  user: {
    profile: () => `/user/profile`,
    settings: () => `/user/settings`,
  },
  events: {
    list: () => `/events`,
    create: () => `/events`,
    show: (id: string) => `/events/${id}`,
    update: (id: string) => `/events/${id}`,
    delete: (id: string) => `/events/${id}`,
    publish: (id: string) => `/events/${id}/publish`,
    unpublish: (id: string) => `/events/${id}/unpublish`,
    qrCode: (id: string) => `/events/${id}/qr-code`,
    share: (id: string) => `/events/${id}/share`,
    landingSettings: (id: string) => `/events/${id}/landing-settings`,
    rsvpSettings: (id: string) => `/events/${id}/rsvp-settings`,
    giftRegistry: (id: string) => `/events/${id}/gift-registry`,
    cover: MEDIA_ROUTES.cover,
    public: (slug: string) => `/events/public/${slug}`,
  },
  gifts: {
    list: (eventId: string) => `/events/${eventId}/gifts`,
    reserve: (giftId: string) => `/gifts/${giftId}/reserve`,
    contributions: (giftId: string) => `/gifts/${giftId}/contributions`,
    image: MEDIA_ROUTES.giftImage,
  },
  guests: {
    list: (eventId: string) => `/events/${eventId}/guests`,
    import: (eventId: string) => `/events/${eventId}/guests/import`,
    sendInvitations: (eventId: string) => `/events/${eventId}/invitations/send`,
  },
  payments: {
    createIntent: () => `/payments/create`,
    confirm: () => `/payments/confirm`,
    testComplete: () => `/payments/test-complete`,
  },
  paymentMethods: {
    list: () => `/payment-methods`,
    create: () => `/payment-methods`,
    update: (id: string) => `/payment-methods/${id}`,
    delete: (id: string) => `/payment-methods/${id}`,
  },
  subscriptions: {
    plans: () => `/subscriptions/plans`,
  },
  templates: {
    list: () => `/templates`,
    apply: (eventId: string) => `/events/${eventId}/template`,
  },
  features: {
    list: () => `/features`,
  },
  management: {
    deletedAccounts: (params?: Record<string, string | number>) => {
      const url = `/management/accounts/deleted`;
      if (!params || Object.keys(params).length === 0) return url;
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      return `${url}?${searchParams.toString()}`;
    },
    deletedEvents: (params?: Record<string, string | number>) => {
      const url = `/management/events/deleted`;
      if (!params || Object.keys(params).length === 0) return url;
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      return `${url}?${searchParams.toString()}`;
    },
    restoreAccount: (id: number) => `/management/accounts/${id}/restore`,
    restoreEvent: (uuid: string) => `/management/events/${uuid}/restore`,
    featureFlags: () => `/management/feature-flags`,
    featureFlagToggle: (key: string) => `/management/feature-flags/${key}`,
    settings: () => `/management/settings`,
    users: (params?: Record<string, string | number>) => {
      const url = `/management/users`;
      if (!params || Object.keys(params).length === 0) return url;
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) searchParams.append(key, String(value));
      });
      return `${url}?${searchParams.toString()}`;
    },
    userProfile: (id: number) => `/management/users/${id}`,
    updateUserStatus: (id: number) => `/management/users/${id}`,
    templates: {
      list: () => `/management/templates`,
      preview: (id: number) => `/management/templates/${id}/preview`,
      update: (id: number) => `/management/templates/${id}`,
      delete: (id: number) => `/management/templates/${id}`,
      install: () => `/management/templates/install`,
    },
    getPlan: (id: number) => `/management/accounts/${id}/plan`,
    updatePlan: (id: number) => `/management/accounts/${id}/plan`,
  },
  rsvp: {
    show: (token: string) => `/rsvp/${token}`,
    submit: (token: string) => `/rsvp/${token}`,
  },
} as const;

